import { Controller } from "@hotwired/stimulus"

// The views of a creator's places — a map, a list, and since 13 Oct 2026 the
// videos filmed there — behind a row of tabs. The list comes first; the map
// and the videos are a tap away.
//
// Deliberately knows nothing about MapLibre, and ships in the main bundle
// rather than alongside the map. If the map bundle fails to load, the other
// tabs still work and every row still opens its modal — the page degrades
// to what it was before the map existed.
//
// Opening a modal is partner-open's job, not this controller's: the creator's
// picker on /brunches wants that and none of this.
export default class extends Controller {
  static targets = ["map", "list", "videos", "tab", "strip"]

  show(event) {
    const view = event.currentTarget.dataset.view
    for (const name of ["map", "list", "videos"]) {
      const has = this[`has${name[0].toUpperCase()}${name.slice(1)}Target`]
      if (has) this[`${name}Target`].classList.toggle("hidden", view !== name)
    }
    this.tabTargets.forEach((tab) => {
      tab.dataset.on = tab.dataset.view === view
      tab.setAttribute("aria-selected", tab.dataset.view === view)
    })
    // MapLibre cannot measure a `display:none` container — it reads 0×0 and
    // renders nothing. Tell it once the box is back on screen.
    if (view === "map") this.dispatch("shown")
  }

  // A map pin, over a DOM event rather than a direct call: the map controller
  // lives in a separate bundle and the two never hold a reference to each other.
  //
  // A pin does not open the modal. It brings the strip to the partner's card,
  // which is the thing that says what the place actually is; opening it is then
  // the visitor's second, deliberate tap.
  focusFromMap(event) {
    if (!this.hasStripTarget) return this.focusRow(event.detail.id)
    const card = this.stripTarget
      .querySelector(`[data-partner-id="${CSS.escape(event.detail.id)}"]`)
      ?.closest("li")
    if (!card) return

    this.stripTarget.querySelectorAll("[data-active]")
      .forEach((other) => delete other.dataset.active)
    card.dataset.active = ""

    // Centred by hand rather than with scrollIntoView, which scrolls every
    // scrollable ancestor of the target — including the page. Tapping a pin
    // would jerk the whole document to bring a strip that is already on screen
    // into view. Setting scrollLeft moves the strip and nothing else.
    //
    // Measured against the strip rather than through offsetLeft: offsetLeft is
    // relative to the nearest positioned ancestor, which here is the map box,
    // so it carries the strip's own inset as a constant error. Rects are
    // relative to the viewport, and the difference of two of them is exact.
    const strip = this.stripTarget
    const offset = card.getBoundingClientRect().left
      - strip.getBoundingClientRect().left
    const centred = strip.scrollLeft + offset
      - (strip.clientWidth - card.offsetWidth) / 2
    strip.scrollTo({ left: centred, behavior: "smooth" })
  }

  focusRow(id) {
    if (!this.hasListTarget) return
    const row = this.listTarget.querySelector(`[data-row="${CSS.escape(id)}"]`)
    if (!row) return
    this.listTarget.querySelectorAll("[data-active]").forEach((other) => delete other.dataset.active)
    row.dataset.active = ""
    row.scrollIntoView({ behavior: "smooth", block: "center" })
  }
}
